import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Stack, Typography, Grid, Skeleton, Card, CardActionArea, Divider, TextField } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { isEqual } from "lodash";
import { Button, IconButton, Tooltip } from "@financeable-com-au/financeable-ui";
import { usePaidServices } from "@/hooks";
import { PaidServicesTooltip } from "@/components/PaidServicesTooltip";
import { openSnackbar } from "@/store/slices/snackbarSlice";
import Document from "./Document";
import CustomCardSending from "./CustomCardSending";
import CreditScoreTypesSelector from "./CreditScoreTypesSelector";
import CreditScoreGradientGraph from "../customComponents/CreditScoreGradientGraph";
import setTicksAndCrosses from "../../utils/setTicksAndCrosses";
import {
  getCreditFile,
  saveValue,
  updateCreditProfile,
  userDetailsSelector,
  applicationSelector,
} from "../../store/slices/applicationFormSlice";
import {
  ERRORS_MESSAGE,
  FILE_AWAITING_MESSAGE,
  SUCCESS_MESSAGE,
  getIsAssetLoan,
  TOOLTIP_MESSAGES,
} from "../../constants";
import { RichTooltip } from "../CommonComponents/RichTooltip";
import { getCustomerRole } from "../Utils/helperFunction";

const dollarStringOptions = {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
};

const CreditProfile = React.memo(({ customer, applicationId, isDisableEdit, customerIndex }) => {
  const { canUsePaidServices } = usePaidServices();
  const dispatch = useDispatch();
  const applicationState = useSelector(applicationSelector);
  const customerRole = getCustomerRole(
    applicationState?.applicationType,
    applicationState?.entities,
    applicationState?.purpose,
  );
  const { gettingCreditProfile, creditScoreTypes, gettingEquifaxCreditFile, updatingCreditScoreModeCusId } =
    useSelector(userDetailsSelector, isEqual);
  const isAssetLoan = getIsAssetLoan(applicationState?.applicationType);
  const creditProfile = customer?.creditProfile;
  const isManualScore = creditProfile?.isManualScore ?? false;
  const [manualScoreValue, setManualScoreValue] = useState(creditProfile?.manualScore ?? "");

  useEffect(() => {
    setManualScoreValue(creditProfile?.manualScore ?? "");
  }, [creditProfile?.manualScore]);

  const disableButton =
    (!customer?.documents?.filter((doc) => doc.name === "Privacy agreement")?.[0] &&
      !customer?.documents?.filter(
        (doc) => doc.name === "SIGNED Credit Guide and Privacy Consent - Consumer Asset Finance and Personal Loans.pdf",
      )?.[0] &&
      !customer?.documents?.filter((doc) => doc.name === "SIGNED Privacy Consent - Asset Finance.pdf")?.[0]) ||
    !customer?.customerTitle ||
    !customer?.firstName ||
    !customer?.lastName ||
    !customer?.IDNumber ||
    (isAssetLoan && !applicationState?.asset?.assetValue) ||
    creditScoreTypes?.length <= 0 ||
    !customer?.addresses?.[0] ||
    gettingCreditProfile ||
    isDisableEdit;

  function totalLimit(creditProfile) {
    const limit = [
      creditProfile?.accounts?.limitsOfAllConsumerAccounts,
      creditProfile?.accounts?.limitsOfCreditCards,
      creditProfile?.accounts?.limitsOfPersonalLoans,
      creditProfile?.accounts?.limitsOfMortgages,
      creditProfile?.accounts?.limitsOfRental,
      creditProfile?.accounts?.limitsOfOverdraft,
      creditProfile?.accounts?.limitsOfCardCards,
      creditProfile?.accounts?.limitsOfAuto,
      creditProfile?.accounts?.limitsOfTelco,
      creditProfile?.accounts?.limitsOfUtilities,
      creditProfile?.accounts?.limitsOfBNPLTransaction,
      creditProfile?.accounts?.limitsOfBNPLFacility,
    ].reduce((acc, limit) => {
      if (limit * 1 < 0) return acc;

      return limit * 1 + acc;
    }, 0);
    return limit;
  }

  const getScoreTitle = (scoreData) => {
    const scoreDetails = scoreData?.["vs:scorecard"];
    // Sample: Equifax One Score 1.0 Comprehensive Consumer
    const creditTitle =
      scoreDetails?.["vs:name"]?.["_text"] +
      " " +
      scoreDetails?.["vs:version"]?.["_text"] +
      " " +
      scoreDetails?.["vs:data-level"]?.["_text"] +
      " " +
      scoreDetails?.["vs:type"]?.["_text"];
    return creditTitle || "";
  };

  const handle = {
    toggleScoreMode: () => {
      dispatch(saveValue({ updatingCreditScoreModeCusId: customer?._id }));
      dispatch(
        updateCreditProfile({
          applicationId,
          customer: customer?._id,
          isManualScore: !isManualScore,
        }),
      ).then((res) => {
        if (res?.error) {
          dispatch(
            openSnackbar({
              message: res?.payload?.status_message || ERRORS_MESSAGE.fetchErrorMsg,
              intent: "danger",
            }),
          );
        }
      });
    },
    handleManualScore: (event) => {
      setManualScoreValue(event.target.value);
    },
    updateCreditScore: (value) => {
      dispatch(
        updateCreditProfile({
          applicationId,
          customer: customer?._id,
          manualScore: value,
        }),
      );
    },
    handleGetCreditFile: () => {
      dispatch(saveValue({ gettingCreditProfile: true }));
      dispatch(
        getCreditFile({
          data: {
            customerId: customer._id,
            applicationId,
            creditScoreTypes,
          },
        }),
      ).then((res) => {
        if (res?.error) {
          dispatch(
            openSnackbar({
              message: res?.payload?.status_message || ERRORS_MESSAGE.fetchErrorMsg,
              intent: "danger",
            }),
          );
        } else {
          dispatch(
            openSnackbar({
              message: res?.payload?.data?.status_message || SUCCESS_MESSAGE.creditFile,
              intent: "success",
            }),
          );
        }
      });
    },
  };

  return (
    <>
      <Grid
        item
        xs={12}
        style={{
          margin: "0 0 30px",
          borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
          paddingBottom: "30px",
        }}
      >
        <Stack direction="row" spacing={3} alignItems="center" style={{ marginBottom: "15px" }}>
          <Typography fontWeight={600}>Credit Profile</Typography>
          {customerIndex === 0 && (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Button
                variant="secondary"
                size="sm"
                onClick={handle.toggleScoreMode}
                disabled={isDisableEdit || updatingCreditScoreModeCusId === customer?._id}
                style={{ fontWeight: 400 }}
              >
                {!isManualScore ? "USE MANUAL SCORE" : "USE EQUIFAX SCORE"}
              </Button>
              <Tooltip
                title={
                  <Stack padding={1} flexDirection="column">
                    <Typography>{TOOLTIP_MESSAGES.CREDIT_PROFILE_SCORE_TOGGLE}</Typography>
                  </Stack>
                }
              >
                <IconButton aria-label="Tooltip" isRounded={true} size="sm" variant="inline">
                  <InfoIcon sx={{ fontSize: "1rem" }} />
                </IconButton>
              </Tooltip>
            </Stack>
          )}
        </Stack>

        <Grid container sx={{ mb: "30px" }} spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              variant="filled"
              type="number"
              name="creditscore"
              label="Score"
              size="small"
              placeholder="800"
              value={manualScoreValue || ""}
              onChange={handle.handleManualScore}
              onBlur={(event) => {
                if (event.target.value !== (creditProfile?.manualScore?.toString() ?? "")) {
                  handle.updateCreditScore(event.target.value);
                }
              }}
              disabled={isDisableEdit}
            />
          </Grid>
        </Grid>

        {/* Enable this after checking failed response */}
        {creditProfile !== undefined &&
          !creditProfile?.isValidConsumerCreditReport &&
          creditProfile?.creditScoreTypes?.length > 0 && (
            <Typography
              variant="subtitle1"
              fontWeight={600}
              textAlign={"center"}
              sx={{ padding: "20px", marginBottom: "20px" }}
            >
              No Equifax data available
            </Typography>
          )}

        {gettingCreditProfile && (
          <>
            <Grid item container spacing={2} style={{ marginBottom: "10px", marginTop: "10px" }}>
              <Grid item sm={4}>
                <Skeleton variant="text" />
                <Skeleton variant="text" />
              </Grid>
              <Grid item sm={4}>
                <Skeleton variant="text" />
                <Skeleton variant="text" />
              </Grid>
            </Grid>
            <Grid item container spacing={2}>
              <Grid item container>
                <Grid item sm={4}>
                  <Skeleton variant="text" />
                </Grid>
                <Grid item container>
                  <Grid item sm={7}>
                    <Skeleton variant="text" />
                  </Grid>
                  <Grid item sm={8}>
                    <Skeleton variant="text" />
                  </Grid>
                  <Grid item sm={9}>
                    <Skeleton variant="text" />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item container>
                <Grid item sm={4}>
                  <Skeleton variant="text" />
                </Grid>
                <Grid item container>
                  <Grid item sm={7}>
                    <Skeleton variant="text" />
                  </Grid>
                  <Grid item sm={8}>
                    <Skeleton variant="text" />
                  </Grid>
                  <Grid item sm={10}>
                    <Skeleton variant="text" />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </>
        )}

        {!gettingCreditProfile && creditProfile && creditProfile?.isValidConsumerCreditReport && (
          <>
            <Stack direction="row" spacing={3} divider={<Divider orientation="vertical" flexItem />}>
              <Grid item sm={7} md={6} xs={12}>
                {creditProfile?.scoreData?.[0] &&
                creditProfile?.scoreData?.[0]?.["vs:key-contributing-factors"]?.["vs:contributing-factor"]?.length >
                  0 ? (
                  <Grid item sm={12} xs={12}>
                    <Typography variant="subtitle2" fontWeight={600} style={{ marginBottom: 5 }}>
                      {/* Equifax One score */}
                      {getScoreTitle(creditProfile?.scoreData?.[0])}
                    </Typography>

                    <Grid container item md={12} sm={12}>
                      {creditProfile?.score && <CreditScoreGradientGraph score={creditProfile.score} />}
                    </Grid>
                  </Grid>
                ) : (
                  <Grid container item md={12} sm={12}>
                    <Stack spacing={1} direction="column" justifyContent="space-between" width={"100%"}>
                      <Typography variant="h6" fontWeight={600} lineHeight={"1.3"}>
                        {getScoreTitle(creditProfile?.scoreData?.[0])}
                      </Typography>
                      <Typography variant="caption">
                        Is not available please check credit report for more information.
                      </Typography>
                    </Stack>
                  </Grid>
                )}

                {creditProfile?.scoreData?.[1] && (
                  <Grid item sm={12} xs={12} style={{ margin: "20px 0 10px" }}>
                    <Stack spacing={1} direction="row" justifyContent="space-between">
                      <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        style={{
                          fontSize: "14px",
                        }}
                      >
                        {/* Comprehensive score: */}
                        {getScoreTitle(creditProfile?.scoreData?.[1])}
                      </Typography>
                      <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        style={{
                          fontSize: "16px",
                          marginRight: "3px",
                        }}
                      >
                        {creditProfile?.scoreData?.[1]?.["vs:score-masterscale"]["_text"]}
                      </Typography>
                    </Stack>
                  </Grid>
                )}

                {creditProfile?.scoreData?.[2] && (
                  <Grid item sm={12} xs={12} margin={"10px 0 10px"}>
                    <Stack spacing={1} direction="row" justifyContent="space-between">
                      <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        style={{
                          fontSize: "14px",
                        }}
                      >
                        {getScoreTitle(creditProfile?.scoreData?.[2])}
                      </Typography>
                      <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        style={{
                          fontSize: "16px",
                          marginRight: "3px",
                        }}
                      >
                        {creditProfile?.scoreData?.[2]?.["vs:score-masterscale"]["_text"]}
                      </Typography>
                    </Stack>
                  </Grid>
                )}

                <Grid container sm={12} margin={"15px 0"}>
                  <Grid container item sm={12}>
                    <Grid item sm={12} xs={12}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="subtitle2" fontWeight={600}>
                          Age of file (years)
                        </Typography>
                        <Typography variant="body2">{(creditProfile?.ageOfFile / 12).toFixed(2) || "0"}</Typography>
                      </Stack>
                    </Grid>
                    <Grid item sm={12} xs={12}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="subtitle2" fontWeight={600}>
                          Adverse on file
                        </Typography>
                        <Typography variant="body2">
                          {creditProfile?.consumerAdverse === "Y" && "Yes"}
                          {creditProfile?.consumerAdverse === "N" && "No"}
                          {!creditProfile?.consumerAdverse && "N/A"}
                        </Typography>
                      </Stack>
                    </Grid>
                    <Grid item sm={12} xs={12}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="subtitle2" fontWeight={600}>
                          Credit enquiries
                        </Typography>
                        <Typography variant="body2">
                          {creditProfile?.enquiries?.["vs:credit-enquiry"]?.length || "0"}
                        </Typography>
                      </Stack>
                    </Grid>
                    <Grid item sm={12} xs={12}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="subtitle2" fontWeight={600}>
                          Accounts
                        </Typography>
                        <Typography variant="body2">{creditProfile?.accounts?.numberOfOpenAccounts || "0"}</Typography>
                      </Stack>
                    </Grid>
                    <Grid item sm={12} xs={12}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="subtitle2" fontWeight={600}>
                          Defaults
                        </Typography>
                        <Typography variant="body2">{creditProfile?.numberOfDefaults || "0"}</Typography>
                      </Stack>
                    </Grid>
                    <Grid item sm={12} xs={12}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="subtitle2" fontWeight={600}>
                          Total limit
                        </Typography>
                        <Typography variant="body2">
                          {totalLimit(creditProfile)?.toLocaleString("en-US", dollarStringOptions)}
                        </Typography>
                      </Stack>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>

              {creditProfile?.scoreData?.[0] &&
                creditProfile?.scoreData?.[0]?.["vs:key-contributing-factors"]?.["vs:contributing-factor"]?.length >
                  0 && (
                  <Grid item sm={5} md={6} xs={12}>
                    <Grid item xs={12} style={{ marginBottom: "15px" }}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Score key contributing factors
                      </Typography>
                    </Grid>
                    <Grid container item spacing={1}>
                      {creditProfile?.scoreData?.[0] &&
                        creditProfile?.scoreData?.[0]?.["vs:key-contributing-factors"]?.["vs:contributing-factor"]?.map(
                          (factor, index) => (
                            <Grid key={factor?.["vs:score-impactor"]?._text || index} container item md={12} sm={12}>
                              <Grid item sm={2} direction="row" style={{ paddingTop: "4px" }}>
                                <Typography variant="subtitle2">
                                  {factor?.["vs:score-impactor"]?._text
                                    ? setTicksAndCrosses(factor["vs:score-impactor"]._text)
                                    : ""}
                                </Typography>
                              </Grid>
                              <Grid item sm={10}>
                                <Stack>
                                  <Typography variant="subtitle2">{factor?.["vs:name"]?._text || ""}</Typography>
                                  <Typography variant="body2">{factor?.["vs:description"]?._text || ""}</Typography>
                                </Stack>
                              </Grid>
                            </Grid>
                          ),
                        )}
                    </Grid>
                  </Grid>
                )}
            </Stack>
          </>
        )}

        {customer?.documents?.filter((doc) => doc.name === "Credit file" || doc.name === "No record credit file")
          ?.length <= 0 &&
          gettingEquifaxCreditFile && (
            <Stack spacing={1} direction="row" margin={"20px 0 0 0"}>
              <Typography variant="caption">{FILE_AWAITING_MESSAGE.gettingEquifaxCreditPDFMsg}</Typography>
            </Stack>
          )}

        <Grid item xs={12} sx={{ minWidth: 0, marginBottom: "20px" }}>
          <Stack spacing={1} direction="row" flexWrap="wrap" useFlexGap>
            {customer?.documents?.length > 0 &&
              customer?.documents
                ?.filter((doc) => doc.name === "Credit file" || doc.name === "No record credit file")
                .map((doc) => <Document document={doc} customer={customer} name="creditProfile" />)}
          </Stack>
        </Grid>

        <Grid container spacing={2} mb={"20px"}>
          <Grid item xs={12}>
            {/* Equifax score id selector */}
            <CreditScoreTypesSelector customer={customer} application={applicationState} />
          </Grid>

          <Grid item xs={12} sm={6}>
            {/* Equifax Request Card */}
            <RichTooltip title={canUsePaidServices ? null : <PaidServicesTooltip />}>
              <Card>
                <CardActionArea
                  disableRipple={!canUsePaidServices || disableButton}
                  onClick={() => {
                    if (!canUsePaidServices || disableButton) return;
                    handle.handleGetCreditFile();
                  }}
                >
                  <CustomCardSending
                    title={"Get Credit file"}
                    disabled={!canUsePaidServices || disableButton}
                    description={` Get ${customer.firstName}'s credit report using Equifax Access Seeker.`}
                    imagePath={"/static/equifax-logo.png"}
                    loadingState={false}
                    financialState={false}
                    icon={false}
                    lastTitle={"Sending SMS link..."}
                  />
                </CardActionArea>
              </Card>

              <Typography variant="caption">
                {disableButton
                  ? `${!gettingEquifaxCreditFile ? "Requires" : ""}  
                ${!customer?.firstName && !customer?.lastName ? `${customerRole} full name, ` : ""}
                ${isAssetLoan && !applicationState?.asset?.assetValue ? "purchase price, " : ""}
                ${!customer?.dateOfBirth ? `DOB, ` : ""}
                ${creditScoreTypes?.length <= 0 ? `Credit score type, ` : ""}
                ${!customer?.customerTitle ? `Title, ` : ""}
                ${!customer?.addresses?.[0] ? `address, ` : ""}
                ${!customer?.IDNumber ? `licence number, ` : ""}
                ${
                  !customer?.documents?.filter((doc) => doc.name === "Privacy agreement")?.[0] &&
                  !customer?.documents?.filter(
                    (doc) =>
                      doc.name ===
                      "SIGNED Credit Guide and Privacy Consent - Consumer Asset Finance and Personal Loans.pdf",
                  )?.[0] &&
                  !customer?.documents?.filter((doc) => doc.name === "SIGNED Privacy Consent - Asset Finance.pdf")?.[0]
                    ? `signed privacy statement.`
                    : ""
                }`
                  : ""}
              </Typography>
            </RichTooltip>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
});

export default CreditProfile;
